import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button } from '../common';

/**
 * MeetingModal - Add or edit meeting
 */
const MeetingModal = ({ meeting, users, onClose, onSave }) => {
  const [editedMeeting, setEditedMeeting] = useState({
    name: meeting?.name || '',
    participants: meeting?.participants || [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('firstName');

  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      const aVal = (a[sortBy] || '').toLowerCase();
      const bVal = (b[sortBy] || '').toLowerCase();
      return aVal.localeCompare(bVal);
    });
  }, [users, sortBy]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (meeting?.id) {
        await api.put(`/meetings/${meeting.id}`, editedMeeting);
      } else {
        await api.post('/meetings', editedMeeting);
      }
      onSave();
    } catch (error) {
      setError(error.message || 'Failed to save meeting');
    } finally {
      setSaving(false);
    }
  };

  const toggleParticipant = (userId) => {
    const newParticipants = editedMeeting.participants.includes(userId)
      ? editedMeeting.participants.filter(id => id !== userId)
      : [...editedMeeting.participants, userId];
    setEditedMeeting({ ...editedMeeting, participants: newParticipants });
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-large">
        <div className="modal-header">
          <h2 className="modal-title">{meeting ? 'Edit Meeting' : 'Create Meeting'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Meeting Name</label>
            <input
              type="text"
              className="form-input"
              value={editedMeeting.name}
              onChange={(e) => setEditedMeeting({ ...editedMeeting, name: e.target.value })}
              placeholder="Enter meeting name"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Participants</label>
            <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>Sort by:</span>
              <Button
                variant={sortBy === 'firstName' ? 'primary' : 'secondary'}
                size="small"
                onClick={() => setSortBy('firstName')}
              >
                First Name
              </Button>
              <Button
                variant={sortBy === 'lastName' ? 'primary' : 'secondary'}
                size="small"
                onClick={() => setSortBy('lastName')}
              >
                Last Name
              </Button>
            </div>
            <div style={{
              maxHeight: '200px',
              overflowY: 'auto',
              border: '1px solid var(--color-border)',
              borderRadius: '4px',
              padding: '0.5rem'
            }}>
              {sortedUsers.map(user => (
                <label
                  key={user.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    padding: '0.25rem'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={editedMeeting.participants.includes(user.id)}
                    onChange={() => toggleParticipant(user.id)}
                  />
                  {user.firstName} {user.lastName}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

MeetingModal.propTypes = {
  meeting: PropTypes.object,
  users: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default MeetingModal;
